import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Chip,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  Stack,
  Paper,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ComplaintsService, { ComplaintStatus } from 'src/services/complaints-service';

interface ComplaintThread {
  id: string;
  title: string;
  status: number;
  createdAt: string;
  lastActivityAt: string;
  unreadByAdminCount: number;
  unreadByTenantCount: number;
  tenantId: string;
  tenantName: string;
}

const ComplaintsListing: React.FC = () => {
  const navigate = useNavigate();
  const complaintsService = ComplaintsService.getInstance();
  const [complaints, setComplaints] = useState<ComplaintThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<number | 'all'>('all');

  const handleViewClick = (thread: ComplaintThread) => {
    navigate(`/complaints/${thread.id}`);
  };

  useEffect(() => {
    fetchComplaints();
  }, [statusFilter]);

  const fetchComplaints = async () => {
    try {
      setLoading(true);
      setError(null);
      const allThreads = await complaintsService.getAllThreads();

      let filtered = allThreads;
      if (statusFilter !== 'all') {
        filtered = filtered.filter((thread) => thread.status === statusFilter);
      }

      // Sort by last activity
      filtered.sort(
        (a, b) =>
          new Date(b.lastActivityAt).getTime() -
          new Date(a.lastActivityAt).getTime()
      );

      setComplaints(filtered);
    } catch (err) {
      setError('Failed to load complaints');
      console.error('Error fetching complaints:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  // Group complaints by tenant
  const groupedComplaints = useMemo(() => {
    const groups = new Map<string, ComplaintThread[]>();

    complaints.forEach((complaint) => {
      const tenantKey = complaint.tenantId;
      if (!groups.has(tenantKey)) {
        groups.set(tenantKey, []);
      }
      groups.get(tenantKey)!.push(complaint);
    });

    return Array.from(groups.entries()).map(([tenantId, threads]) => ({
      tenantId,
      tenantName: threads[0].tenantName,
      threads,
      totalUnread: threads.reduce((sum, t) => sum + t.unreadByAdminCount, 0)
    }));
  }, [complaints]);

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight={700}>
            Tenant Complaints
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage and respond to tenant messages and complaints
          </Typography>
        </Box>
        <IconButton
          onClick={fetchComplaints}
          disabled={loading}
          sx={{
            bgcolor: 'primary.lighter',
            '&:hover': { bgcolor: 'primary.light' }
          }}
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            select
            label="Filter by Status"
            size="small"
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(
                e.target.value === 'all' ? 'all' : Number(e.target.value)
              )
            }
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value={ComplaintStatus.Open}>
              {complaintsService.getStatusLabel(ComplaintStatus.Open)}
            </MenuItem>
            <MenuItem value={ComplaintStatus.InProgress}>
              {complaintsService.getStatusLabel(ComplaintStatus.InProgress)}
            </MenuItem>
            <MenuItem value={ComplaintStatus.Resolved}>
              {complaintsService.getStatusLabel(ComplaintStatus.Resolved)}
            </MenuItem>
            <MenuItem value={ComplaintStatus.Closed}>
              {complaintsService.getStatusLabel(ComplaintStatus.Closed)}
            </MenuItem>
          </TextField>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : groupedComplaints.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No complaints found
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {groupedComplaints.map((group) => (
            <Accordion
              key={group.tenantId}
              defaultExpanded
              sx={{
                boxShadow: 2,
                '&:before': { display: 'none' },
                borderRadius: 2,
                overflow: 'hidden'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  bgcolor: 'background.default',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: 'primary.main',
                      fontSize: '0.875rem',
                      fontWeight: 600
                    }}
                  >
                    {group.tenantName
                      .split(' ')
                      .map((name) => name.charAt(0))
                      .join('')
                      .toUpperCase()
                      .substring(0, 2)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight={600}>
                      {group.tenantName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {group.threads.length} complaint{group.threads.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  {group.totalUnread > 0 && (
                    <Chip
                      label={`${group.totalUnread} unread`}
                      size="small"
                      color="error"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'background.default' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Last Activity</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {group.threads.map((thread) => (
                        <TableRow
                          key={thread.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleViewClick(thread)}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" noWrap>
                                {thread.title}
                              </Typography>
                              {thread.unreadByAdminCount > 0 && (
                                <Chip
                                  label={`${thread.unreadByAdminCount} new`}
                                  size="small"
                                  color="error"
                                  sx={{ height: 20, fontSize: '0.65rem' }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={complaintsService.getStatusLabel(thread.status)}
                              size="small"
                              color={complaintsService.getStatusColor(thread.status)}
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatTimeAgo(thread.lastActivityAt)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(thread.createdAt).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip title="View Thread">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewClick(thread);
                                }}
                                sx={{
                                  color: 'primary.main',
                                  '&:hover': { bgcolor: 'primary.lighter' }
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ComplaintsListing;
